import React, { useMemo } from 'react';
import { ApolloProvider, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { SnackbarProvider } from 'notistack';
import client from '../../../lib/apolloClient';
import { getAvatarUrl } from '../../../lib/s3';
import configureStore from '../../../store/findBobPublicStore';
import translations from '../../../lib/i18n/translations.json';
import SuccessionThemeProvider from '../SuccessionThemeProvider';
import SuccessionDrawerWithListener from '../../../containers/Succession/SuccessionDrawerWithListener';

/**
 * ManagerViewContainer — /succession/manager-view
 * Roll-up for an approver of the advisors reporting to them: onboarding status,
 * motivation, chosen goals, approval state (+ link to the tokenized approval
 * page), roll-up metrics, and — on row click — the SAME profile drawer the
 * marketplace/search uses, opened by setting `document.x.a = fb_user_name` with
 * `SuccessionDrawerWithListener` mounted below (redux store + theme provider).
 *
 * Plan: docs/plans/2026-07-23-feat-succession-manager-view-plan.md
 */

const GET_REPORTING_ADVISORS = gql`
  query ManagerViewAdvisors {
    approverReportingAdvisors {
      id
      name
      fb_user_name
      profile_picture
      onboarded
      user_motivation
      transition_goals_name
      approval_state
      pending_motivation
      pending_change_label
      pending_transition_goals_name
    }
  }
`;

const MOTIVATION_STYLE = {
  growth: { background: '#dbeafe', color: '#1d4ed8' },
  succession: { background: '#ede9fe', color: '#6d28d9' },
  no_motivation: { background: '#f3f4f6', color: '#6b7280' },
};

const APPROVAL_STYLE = {
  approved: { background: '#dcfce7', color: '#15803d' },
  pending: { background: '#ffedd5', color: '#c2410c' },
  denied: { background: '#fee2e2', color: '#b91c1c' },
};

const titleize = (s) => (s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');
const goalNames = (g) => (Array.isArray(g) && g.length ? g.join(', ') : '—');

// Opens the shared marketplace profile drawer (SuccessionDrawerWithListener
// listens on document.x.a). No-op for advisors without an onboarded account.
const openProfileDrawer = (fbUserName) => {
  if (fbUserName && typeof document !== 'undefined' && document.x) {
    document.x.a = fbUserName;
  }
};

function Pill({ text, style }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
      style={style || { background: '#f3f4f6', color: '#6b7280' }}
    >
      {text}
    </span>
  );
}

function Metrics({ advisors }) {
  const stats = useMemo(() => {
    const by = (m) => advisors.filter((a) => (a.user_motivation || 'no_motivation') === m).length;
    return {
      total: advisors.length,
      onboarded: advisors.filter((a) => a.onboarded).length,
      succession: by('succession'),
      growth: by('growth'),
      no_motivation: by('no_motivation'),
    };
  }, [advisors]);

  const cards = [
    { label: 'Total advisors', value: stats.total },
    { label: 'Onboarded users', value: stats.onboarded },
    { label: 'Succession', value: stats.succession },
    { label: 'Growth', value: stats.growth },
    { label: 'Here for research', value: stats.no_motivation },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-lg p-4"
          style={{ border: '1px solid var(--succession-border, #e5e7eb)', background: 'var(--succession-bg-primary, #fff)' }}
        >
          <div className="text-2xl font-bold" style={{ color: 'var(--succession-text-primary, #111827)' }}>{c.value}</div>
          <div className="text-xs mt-1" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

function ManagerViewInner(props = {}) {
  const isApprover = props.is_approver || false;
  const approvalUrl = props.approval_url || null;

  const { data, loading, error } = useQuery(GET_REPORTING_ADVISORS, { skip: !isApprover });
  const advisors = data?.approverReportingAdvisors || [];

  const wrap = (children) => <div className="max-w-6xl mx-auto px-6 py-8">{children}</div>;

  if (!isApprover) {
    return wrap(
      <div className="text-center py-20" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
        <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--succession-text-primary, #111827)' }}>Manager View</h1>
        <p>No advisors report to you, so there is nothing to show here.</p>
      </div>
    );
  }

  return wrap(
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--succession-text-primary, #111827)' }}>Manager View</h1>
          <p className="text-sm" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>Advisors reporting to you and their chosen goals.</p>
        </div>
        {approvalUrl && (
          <a
            href={approvalUrl}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white"
            style={{ background: 'var(--succession-primary, #4f46e5)' }}
          >
            Review &amp; approve →
          </a>
        )}
      </div>

      <Metrics advisors={advisors} />

      {loading && <div className="py-12 text-center" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>Loading advisors…</div>}
      {error && <div className="rounded-md border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm">Failed to load advisors: {error.message}</div>}

      {!loading && !error && (
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--succession-border, #e5e7eb)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--succession-bg-secondary, #f9fafb)', color: 'var(--succession-text-secondary, #6b7280)' }}>
                <th className="text-left font-medium px-4 py-2">Advisor</th>
                <th className="text-left font-medium px-4 py-2">Onboarded</th>
                <th className="text-left font-medium px-4 py-2">Motivation</th>
                <th className="text-left font-medium px-4 py-2">Goal(s)</th>
                <th className="text-left font-medium px-4 py-2">Approval</th>
              </tr>
            </thead>
            <tbody>
              {advisors.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: 'var(--succession-text-muted, #9ca3af)' }}>No advisors found.</td></tr>
              )}
              {advisors.map((a) => {
                const clickable = !!a.fb_user_name;
                return (
                  <tr
                    key={a.id}
                    className={clickable ? 'cursor-pointer' : ''}
                    style={{ borderTop: '1px solid var(--succession-border, #e5e7eb)' }}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    title={clickable ? 'View profile' : 'No profile — advisor not onboarded'}
                    onClick={() => openProfileDrawer(a.fb_user_name)}
                    onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openProfileDrawer(a.fb_user_name); } }}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {a.profile_picture ? (
                          <img src={getAvatarUrl(a.profile_picture)} alt={a.name} className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          <div className="h-6 w-6 rounded-full bg-gray-100" />
                        )}
                        <span style={{ color: 'var(--succession-text-primary, #111827)' }}>{a.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {a.onboarded
                        ? <Pill text="Onboarded" style={{ background: '#dcfce7', color: '#15803d' }} />
                        : <Pill text="Not onboarded" style={{ background: '#f3f4f6', color: '#6b7280' }} />}
                    </td>
                    <td className="px-4 py-2.5">
                      {a.pending_change_label ? (
                        <span
                          className="inline-block rounded text-xs"
                          style={{ padding: '2px 8px', background: '#fff4d6', borderLeft: '3px solid #d99300', color: '#7c5700' }}
                          title={a.pending_change_label}
                        >
                          {a.pending_change_label}
                        </span>
                      ) : (
                        <Pill text={titleize(a.user_motivation || 'no_motivation')} style={MOTIVATION_STYLE[a.user_motivation || 'no_motivation']} />
                      )}
                    </td>
                    <td className="px-4 py-2.5" style={{ color: 'var(--succession-text-secondary, #6b7280)' }}>
                      {Array.isArray(a.pending_transition_goals_name) && a.pending_transition_goals_name.length > 0 ? (
                        <span>
                          <span className="line-through opacity-60">{goalNames(a.transition_goals_name)}</span>
                          {' → '}
                          <b style={{ color: 'var(--succession-text-primary, #111827)' }}>{goalNames(a.pending_transition_goals_name)}</b>
                        </span>
                      ) : (
                        goalNames(a.transition_goals_name)
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Pill text={titleize(a.approval_state)} style={APPROVAL_STYLE[a.approval_state]} />
                        {approvalUrl && a.approval_state === 'pending' && (
                          <a href={approvalUrl} onClick={(e) => e.stopPropagation()} className="text-xs underline" style={{ color: 'var(--succession-accent, #6366f1)' }}>Review</a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const ManagerViewContainer = (props = {}) => {
  const locale = (typeof window !== 'undefined' && window.locale) || 'en';
  const company = props.company || {};
  const user = props.current_user || props.currentUser || null;
  const store = configureStore({ company });

  return (
    <Provider store={store}>
      <ApolloProvider client={client}>
        <IntlProvider locale={locale} messages={translations[locale] || translations.en}>
          <SnackbarProvider maxSnack={3}>
            <SuccessionThemeProvider company={company} user={user}>
              <ManagerViewInner is_approver={props.is_approver} approval_url={props.approval_url} />
              {/* Shared marketplace profile drawer — opens on document.x.a */}
              <SuccessionDrawerWithListener />
            </SuccessionThemeProvider>
          </SnackbarProvider>
        </IntlProvider>
      </ApolloProvider>
    </Provider>
  );
};

export default ManagerViewContainer;
